/**
 * Instructor self-registration.
 *
 * A teacher signs themselves up and says what they teach — nobody creates the
 * account for them and hands over a password. An admin then verifies the person
 * and the subjects claimed, which is why this deliberately does NOT sign them
 * in: a session that lands on a dashboard where every action is refused reads
 * as a broken product.
 */
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, apiGet, apiPost } from "../api/client";
import { AuthLayout } from "../components/AuthLayout";

type AvailableSubject = {
  id: string;
  title: string;
  grade_label: string;
};

export function RegisterInstructorPage() {
  const [form, setForm] = useState({ username: "", name: "", password: "", email: "" });
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data: subjects } = useQuery({
    queryKey: ["available-subjects"],
    queryFn: () => apiGet<AvailableSubject[]>("/api/curricula/available"),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  function toggle(id: string) {
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (picked.length === 0) {
      setError("Choose at least one subject you teach.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await apiPost("/api/auth/register-instructor", { ...form, subject_ids: picked });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not submit the application");
    } finally {
      setLoading(false);
    }
  }

  function set(key: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));
  }

  if (submitted) {
    return (
      <AuthLayout
        title="Application received"
        intro="An administrator will confirm the subjects you teach. You'll be able to sign in once they have."
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <></>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Teach on PyDialog"
      intro="Tell us what you teach. An administrator confirms it before your account opens."
      footer={
        <>
          <span className="auth-footer-alt">
            Here to learn instead? <Link to="/register">Create a learner account</Link>
          </span>
          <span className="auth-footer-alt">
            Already approved? <Link to="/login">Sign in</Link>
          </span>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="auth-form">
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        <label>
          <span className="field-label">Full name</span>
          <input value={form.name} onChange={set("name")} autoComplete="name" autoFocus required />
          <span className="field-hint">
            Shown to learners as the teacher of your courses.
          </span>
        </label>
        <label>
          <span className="field-label">Username</span>
          <input
            value={form.username}
            onChange={set("username")}
            autoComplete="username"
            required
          />
        </label>
        <label>
          <span className="field-label">Email</span>
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            autoComplete="email"
          />
          <span className="field-hint">How you'll be told the outcome.</span>
        </label>
        <label>
          <span className="field-label">Password</span>
          <input
            type="password"
            value={form.password}
            onChange={set("password")}
            autoComplete="new-password"
            minLength={6}
            required
          />
          <span className="field-hint">At least 6 characters. You choose it, not an admin.</span>
        </label>

        <fieldset className="subject-pick">
          <legend className="field-label">Subjects you teach</legend>
          <span className="field-hint">
            Choose every one you can offer. This is what the administrator checks.
          </span>
          <ul>
            {(subjects ?? []).map((s) => (
              <li key={s.id} className={picked.includes(s.id) ? "is-picked" : undefined}>
                <label className="subject-row">
                  <input
                    type="checkbox"
                    checked={picked.includes(s.id)}
                    onChange={() => toggle(s.id)}
                  />
                  <span className="subject-name">
                    {s.title}
                    {s.grade_label && <span className="subject-grade"> · {s.grade_label}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </fieldset>

        <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
          {loading ? "Submitting…" : "Apply to teach"}
        </button>
      </form>
    </AuthLayout>
  );
}
